import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Trash2 } from "lucide-react";

/**
 * ConfirmDeleteDialog — in-app confirm + reason capture.
 *
 * Props:
 *   open (bool), onOpenChange(fn)
 *   title, subject   — headline + subject line
 *   description      — optional muted subtitle
 *   confirmLabel     — button label (default: "Delete")
 *   requireReason    — force a non-empty reason (default: false)
 *   onConfirm(reason)-> Promise. Return truthy on success; dialog will close.
 */
export default function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title = "Confirm delete",
  subject,
  description = "This action is recorded in the audit log.",
  confirmLabel = "Delete",
  requireReason = false,
  onConfirm,
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (open) { setReason(""); setErr(""); setBusy(false); } }, [open]);

  const submit = async () => {
    if (requireReason && !reason.trim()) {
      setErr("Please enter a reason before deleting.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const ok = await onConfirm(reason.trim());
      if (ok !== false) onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="rounded-md max-w-md" data-testid="confirm-delete-dialog" aria-describedby="confirm-delete-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <AlertTriangle size={18} className="text-primary" /> {title}
          </DialogTitle>
          <p id="confirm-delete-desc" className="text-xs text-muted-foreground">{description}</p>
        </DialogHeader>
        <div className="space-y-3">
          {subject && (
            <div className="text-sm border border-border rounded-md p-3 bg-muted/30">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Subject</div>
              <div className="font-mono text-foreground break-all">{subject}</div>
            </div>
          )}
          <label className="block">
            <span className="overline">Reason {requireReason && <span className="text-primary">*</span>}</span>
            <Textarea
              data-testid="confirm-delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. duplicate entry / customer cancelled / wrong recipient"
              className="mt-1 rounded-md border-input focus-visible:ring-primary"
            />
          </label>
          {err && <div className="text-xs text-primary bg-red-50 border border-primary/30 rounded-md p-2" data-testid="confirm-delete-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" data-testid="confirm-delete-cancel"
              onClick={() => !busy && onOpenChange(false)} disabled={busy} className="rounded-md">
              Cancel
            </Button>
            <Button type="button" data-testid="confirm-delete-submit"
              onClick={submit} disabled={busy}
              className="rounded-md bg-primary text-primary-foreground hover:bg-primary/90">
              <Trash2 size={14} className="mr-1.5" /> {busy ? "Deleting…" : confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
